"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { useState, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import SiteNav from "./SiteNav";
import ProductPanel from "./ProductPanel";

// The scroll-driven 3D hero is WebGL (three.js / react-three-fiber) and must
// never run on the server — load it client-only, mirroring the landing
// HeroExperience pattern so the marketing bundle stays lean on other routes.
const MethodologyHero3D = dynamic(() => import("./MethodologyHero3D"), { ssr: false });

/* ──────────────────────────────────────────────────────────
   Types
────────────────────────────────────────────────────────── */

type ExampleFactor = { name: string; description: string };

type Dimension = {
  id: string;
  name: string;
  accent: string;
  accentSoft: string;
  whatItMeasures: string;
  whyItMatters: string;
  exampleFactors: ExampleFactor[];
  factorCount: string; // e.g., "5 factors"
  icon: React.ReactNode;
};

type Cap = {
  risk: string;
  capValue: number;
  icon: React.ReactNode;
  description: string;
};

type Flag = {
  risk: string;
  icon: React.ReactNode;
  description: string;
};

type FutureFeature = { title: string; description: string };

export type MethodologyContent = {
  locale: "en" | "nl";
  homeHref: string;
  waitlistHref: string;
  altHref: string; // link to the other locale version
  navCta: string;

  heroEyebrow: string;
  heroTitle: string;
  heroSubtitle: string;
  stats: { value: string; label: string }[];

  howEyebrow: string;
  howTitle: string;
  howIntro: string;
  steps: { number: string; title: string; body: string }[];

  dimEyebrow: string;
  dimTitle: string;
  dimIntro: string;
  dimensions: Dimension[];

  confEyebrow: string;
  confTitle: string;
  confBody: string[];
  confExamples: { label: string; coverage: number; score: number; adjusted?: boolean }[];

  capsEyebrow: string;
  capsTitle: string;
  capsBody: string;
  caps: Cap[];

  flagsEyebrow: string;
  flagsTitle: string;
  flagsBody: string;
  flags: Flag[];

  futureEyebrow: string;
  futureTitle: string;
  futureIntro: string;
  futureFeatures: FutureFeature[];

  closingEyebrow: string;
  closingTitle: string;
  closingBody: string;
  closingCta: string;

  footer: { location: string; home: string; privacy: string; terms: string };
};

/* ──────────────────────────────────────────────────────────
   Helpers
────────────────────────────────────────────────────────── */

const EASE = [0.25, 0.1, 0.25, 1] as const;

/** Dimension ids that have a real captured app panel in /public/product. */
const SIDEBAR_SHOTS = new Set([
  "valuation",
  "building",
  "location",
  "neighborhood",
  "investment",
]);

function FadeIn({ children, delay = 0, className }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/* ──────────────────────────────────────────────────────────
   Sub-components
────────────────────────────────────────────────────────── */

/* ── Deal-breaker bar ── */
function CapBar({ originalScore, capValue, accent }: {
  originalScore: number;
  capValue: number;
  accent: string;
}) {
  return (
    <div className="relative h-3 bg-brand-moss rounded-full overflow-hidden">
      {/* Original score (dotted extension) */}
      <div
        className="absolute inset-y-0 left-0 rounded-full opacity-20"
        style={{ width: `${originalScore}%`, background: accent }}
      />
      {/* Capped score (solid) */}
      <div
        className="absolute inset-y-0 left-0 rounded-full"
        style={{ width: `${capValue}%`, background: accent }}
      />
      {/* Cap line */}
      <div
        className="absolute top-[-4px] bottom-[-4px] w-[2px]"
        // Cap marker is a status colour, not a dimension accent → token.
        // Dark `--c-negative` is 248 113 113, i.e. the #f87171 this replaces,
        // so dark renders identically and light gets a legible red.
        style={{ left: `${capValue}%`, background: "rgb(var(--c-negative))" }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   Main Component
────────────────────────────────────────────────────────── */

export default function MethodologyPage({ content }: { content: MethodologyContent }) {
  const c = content;
  const [activeDim, setActiveDim] = useState(0);

  return (
    <div className="content-body font-brand antialiased text-brand-linen">
      {/* ── Nav (shared across all public pages) ── */}
      {/* onDarkHero: MethodologyHero3D is a dark framed surface. */}
      <SiteNav locale={c.locale} active="score" altHref={c.altHref} onDarkHero />

      <main>
        {/* SEO h1 — server-rendered. The visual hero title lives inside the
            WebGL hero (loaded ssr:false), so it isn't in the crawlable HTML;
            this hidden h1 keeps the page's primary heading present for
            crawlers and screen readers. Mirrors the landing page pattern. */}
        <h1 className="sr-only">{c.heroTitle}</h1>

        {/* ═══════════════════════════════════════════
            1. HERO — scroll-driven 3D dive through the five layers
        ═══════════════════════════════════════════ */}
        <MethodologyHero3D content={c} />

        {/* ═══════════════════════════════════════════
            2. HOW IT WORKS
        ═══════════════════════════════════════════ */}
        <section className="py-16 sm:py-24 border-b border-brand-border" style={{ background: "rgb(var(--c-surface) / 0.4)" }}>
          <div className="max-w-[960px] mx-auto px-6 sm:px-8">
            <FadeIn>
              <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.18em] mb-4">{c.howEyebrow}</p>
              <h2 className="font-display text-[1.75rem] sm:text-[2.25rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-4 max-w-[560px]">
                {c.howTitle}
              </h2>
              <p className="text-[0.95rem] text-brand-linen/70 leading-[1.7] mb-12 max-w-[520px]">{c.howIntro}</p>
            </FadeIn>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              {c.steps.map((step, i) => (
                <FadeIn key={step.number} delay={0.1 + i * 0.12}>
                  <div className="relative">
                    {/* Connector line (not on last) */}
                    {i < c.steps.length - 1 && (
                      <div className="hidden sm:block absolute top-6 left-full w-8 h-px bg-brand-border z-0" />
                    )}
                    <div className="relative z-10">
                      <div className="w-10 h-10 rounded-full border border-brand-meadow/30 flex items-center justify-center mb-4">
                        <span className="font-data text-[0.85rem] text-brand-meadow font-semibold">{step.number}</span>
                      </div>
                      <h3 className="font-display text-[1.15rem] font-semibold text-brand-linen mb-2">{step.title}</h3>
                      <p className="text-[0.85rem] text-brand-linen/65 leading-[1.65]">{step.body}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            3. FIVE DIMENSIONS (Tab-based)
        ═══════════════════════════════════════════ */}
        <section className="py-16 sm:py-24 md:py-32 border-b border-brand-border">
          <div className="max-w-[960px] mx-auto px-6 sm:px-8">
            <FadeIn>
              <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.18em] mb-4">{c.dimEyebrow}</p>
              <h2 className="font-display text-[1.75rem] sm:text-[2.25rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-4 max-w-[600px]">
                {c.dimTitle}
              </h2>
              <p className="text-[0.95rem] text-brand-linen/70 leading-[1.7] mb-10 max-w-[520px]">{c.dimIntro}</p>
            </FadeIn>

            {/* Tab bar */}
            <FadeIn delay={0.15}>
              <div className="flex gap-1 border-b border-brand-border mb-8 overflow-x-auto pb-px">
                {c.dimensions.map((d, i) => {
                  const isActive = activeDim === i;
                  return (
                    <button
                      key={d.id}
                      onClick={() => setActiveDim(i)}
                      className="flex items-center gap-2 px-3 sm:px-4 py-3 text-[0.8rem] sm:text-[0.85rem] font-semibold whitespace-nowrap transition-all duration-200 border-b-[2.5px] -mb-px"
                      style={{
                        // Tab chrome flips with the theme; the underline keeps
                        // the dimension's identity accent.
                        borderColor: isActive ? d.accent : "transparent",
                        color: isActive ? "rgb(var(--c-ink))" : "rgb(var(--c-ink-muted))",
                      }}
                    >
                      <div
                        className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 transition-all duration-200"
                        style={{
                          background: isActive ? d.accentSoft : "transparent",
                          color: isActive ? d.accent : "rgb(var(--c-ink-muted))",
                        }}
                      >
                        {d.icon}
                      </div>
                      <span className="hidden sm:inline">{d.name}</span>
                    </button>
                  );
                })}
              </div>
            </FadeIn>

            {/* Tab content */}
            <AnimatePresence mode="wait">
              {c.dimensions.map((d, i) =>
                activeDim === i ? (
                  <motion.div
                    key={d.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.35, ease: EASE }}
                  >
                    <div className="flex flex-col md:flex-row gap-8 md:gap-12">
                      {/* Left: text content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          {/* The accent hex is the dark identity colour; as ICON/TEXT
                              on a themed card it goes through the per-dimension tab
                              token, whose light value clears AA (amber #fbbf24 is
                              1.6:1 on paper). The soft fill keeps the hue. */}
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ background: d.accentSoft, color: `rgb(var(--c-tab-${d.id === "neighborhood" ? "planning" : d.id}))` }}
                          >
                            {d.icon}
                          </div>
                          <div>
                            <h3 className="font-display text-[1.3rem] font-semibold text-brand-linen">{d.name}</h3>
                            <span className="font-data text-[0.65rem] text-brand-muted uppercase tracking-[0.1em]">{d.factorCount}</span>
                          </div>
                        </div>

                        <p className="text-[0.9rem] text-brand-linen/80 leading-[1.7] mb-4">{d.whatItMeasures}</p>
                        <p className="text-[0.85rem] text-brand-linen/60 leading-[1.65] mb-6">{d.whyItMatters}</p>

                        {/* Example factors */}
                        <div className="border-t border-brand-border pt-4">
                          <p className="text-[0.6rem] text-brand-muted uppercase tracking-[0.15em] mb-3">Representative factors</p>
                          <div className="space-y-3">
                            {d.exampleFactors.map((f) => (
                              <div key={f.name} className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: d.accent }} />
                                <div>
                                  <span className="text-[0.85rem] text-brand-linen font-medium">{f.name}</span>
                                  <span className="text-[0.8rem] text-brand-muted ml-1.5">{f.description}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Right: mini visualization + the real captured panel */}
                      <div className="md:w-[260px] flex-shrink-0">
                        <DimensionViz dim={d} />
                        {SIDEBAR_SHOTS.has(d.id) && (
                          <div className="mt-6 max-w-[260px] mx-auto md:mx-0">
                            <ProductPanel
                              src={`/product/sidebar-${d.id}.png`}
                              alt={
                                c.locale === "nl"
                                  ? `De echte ${d.name}-laag in de PlotScore-app — live data voor Laurierstraat 218, Amsterdam`
                                  : `The real ${d.name} layer in the PlotScore app — live data for Laurierstraat 218, Amsterdam`
                              }
                              caption={
                                c.locale === "nl"
                                  ? "Het echte werk — live uit de app"
                                  : "The real thing — live from the app"
                              }
                              sizes="260px"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ) : null
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            5. CONFIDENCE & DATA QUALITY
        ═══════════════════════════════════════════ */}
        <section className="py-16 sm:py-24 border-b border-brand-border">
          <div className="max-w-[820px] mx-auto px-6 sm:px-8">
            <FadeIn>
              <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.18em] mb-4">{c.confEyebrow}</p>
              <h2 className="font-display text-[1.75rem] sm:text-[2.25rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-6 max-w-[560px]">
                {c.confTitle}
              </h2>
            </FadeIn>

            {c.confBody.map((para, i) => (
              <FadeIn key={i} delay={0.1 + i * 0.08}>
                <p className="text-[0.9rem] text-brand-linen/75 leading-[1.75] mb-4">{para}</p>
              </FadeIn>
            ))}

            {/* Confidence examples */}
            <FadeIn delay={0.3}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
                {c.confExamples.map((ex) => (
                  <div key={ex.label} className="border border-brand-border rounded-xl p-5" style={{ background: "rgb(var(--c-surface) / 0.5)" }}>
                    <p className="text-[0.7rem] text-brand-muted uppercase tracking-[0.12em] mb-3">{ex.label}</p>
                    <div className="flex items-end gap-4 mb-3">
                      {/* Confidence state (adjusted vs full) is a status colour,
                          not a dimension accent. Dark tokens equal the hexes
                          they replace (#fbbf24 / #4CAF7A); light needs the
                          swap — raw meadow is 2.3:1 on white, so the TEXT uses
                          accent-ink and only the bar below uses accent. */}
                      <span className="font-data text-[2rem] font-semibold tabular-nums" style={{ color: ex.adjusted ? "rgb(var(--c-warning))" : "rgb(var(--c-accent-ink))" }}>
                        {ex.score}
                      </span>
                      <span className="font-data text-[0.7rem] text-brand-muted mb-1.5">
                        {ex.coverage}% data coverage
                      </span>
                    </div>
                    <div className="h-2 bg-brand-moss rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${ex.coverage}%`,
                          background: ex.adjusted ? "rgb(var(--c-warning))" : "rgb(var(--c-accent))",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            6. DEAL-BREAKER CAPS
        ═══════════════════════════════════════════ */}
        <section className="py-16 sm:py-24 border-b border-brand-border" style={{ background: "rgb(var(--c-surface) / 0.4)" }}>
          <div className="max-w-[820px] mx-auto px-6 sm:px-8">
            <FadeIn>
              <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.18em] mb-4">{c.capsEyebrow}</p>
              <h2 className="font-display text-[1.75rem] sm:text-[2.25rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-4 max-w-[560px]">
                {c.capsTitle}
              </h2>
              <p className="text-[0.9rem] text-brand-linen/75 leading-[1.75] mb-8 max-w-[520px]">{c.capsBody}</p>
            </FadeIn>

            <div className="space-y-5">
              {c.caps.map((cap, i) => (
                <FadeIn key={cap.risk} delay={0.1 + i * 0.1}>
                  <div className="border border-brand-border rounded-xl p-5" style={{ background: "rgb(var(--c-surface) / 0.5)" }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-negative/10 flex items-center justify-center text-negative">
                        {cap.icon}
                      </div>
                      <div>
                        <h4 className="text-[0.9rem] font-semibold text-brand-linen">{cap.risk}</h4>
                        <p className="text-[0.75rem] text-brand-muted">{cap.description}</p>
                      </div>
                    </div>
                    {/* Bar fill, not text → `accent` (dark = the #4CAF7A it replaces) */}
                    <CapBar originalScore={82} capValue={cap.capValue} accent="rgb(var(--c-accent))" />
                    <div className="flex justify-between mt-1.5">
                      <span className="font-data text-[0.65rem] text-brand-muted">0</span>
                      <span className="font-data text-[0.65rem] text-negative">capped at {cap.capValue}</span>
                      <span className="font-data text-[0.65rem] text-brand-muted/40">82 (original)</span>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            6b. RISK FLAGS (non-capping advisories)
        ═══════════════════════════════════════════ */}
        {/* The advisory warmth of this section used to be baked dark-amber
            slabs (rgba(20,18,8,…)), which are black bars on paper. Expressed
            as a low-alpha `warning` wash it survives both themes; dark
            `--c-warning` is 251 191 36 — the #fbbf24 these lines replace. */}
        <section className="py-16 sm:py-24 border-b border-brand-border" style={{ background: "rgb(var(--c-warning) / 0.05)" }}>
          <div className="max-w-[820px] mx-auto px-6 sm:px-8">
            <FadeIn>
              <p className="text-[0.75rem] uppercase tracking-[0.18em] mb-4" style={{ color: "rgb(var(--c-warning))" }}>{c.flagsEyebrow}</p>
              <h2 className="font-display text-[1.75rem] sm:text-[2.25rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-4 max-w-[560px]">
                {c.flagsTitle}
              </h2>
              <p className="text-[0.9rem] text-brand-linen/75 leading-[1.75] mb-8 max-w-[520px]">{c.flagsBody}</p>
            </FadeIn>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {c.flags.map((flag, i) => (
                <FadeIn key={flag.risk} delay={0.1 + i * 0.08}>
                  <div className="border rounded-xl p-5 h-full" style={{ background: "rgb(var(--c-warning) / 0.07)", borderColor: "rgb(var(--c-warning) / 0.25)" }}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgb(var(--c-warning) / 0.1)", color: "rgb(var(--c-warning))" }}>
                        {flag.icon}
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-[0.9rem] font-semibold text-brand-linen mb-1">{flag.risk}</h4>
                        <p className="text-[0.75rem] text-brand-linen/70 leading-[1.6]">{flag.description}</p>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            7. MARKET INTELLIGENCE
        ═══════════════════════════════════════════ */}
        <section className="py-16 sm:py-24 md:py-32 border-b border-brand-border relative">
          <div className="max-w-[960px] mx-auto px-6 sm:px-8">
            {/* No sample transaction table here, deliberately. This page's whole
                claim is provenance, so it can't be the one illustrated with
                invented numbers — and it can't be fixed by wiring real ones in
                either: naming addresses against sale prices is exactly what the
                data-privacy rule forbids in the UI, and what /api/market-estimate
                withholds by returning only { perSqm, confidence }. The prose and
                the capability are real; the table never was. */}
            <FadeIn>
              <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.18em] mb-4">{c.futureEyebrow}</p>
              <h2 className="font-display text-[1.75rem] sm:text-[2.25rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-4 max-w-[600px]">
                {c.futureTitle}
              </h2>
              <p className="text-[0.95rem] text-brand-linen/70 leading-[1.7] max-w-[520px]">{c.futureIntro}</p>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
                {c.futureFeatures.map((ff) => (
                  <div key={ff.title} className="border border-brand-border/60 rounded-xl p-4 bg-brand-pine/30">
                    <h4 className="text-[0.85rem] font-semibold text-brand-linen/80 mb-1">{ff.title}</h4>
                    <p className="text-[0.75rem] text-brand-muted leading-[1.6]">{ff.description}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            8. CLOSING CTA
        ═══════════════════════════════════════════ */}
        <section className="py-20 sm:py-32 bg-brand-pine border-t border-brand-border">
          <div className="max-w-[720px] mx-auto px-6 sm:px-8 text-center">
            <FadeIn>
              <p className="text-[0.75rem] text-brand-meadow uppercase tracking-[0.18em] mb-4">{c.closingEyebrow}</p>
              <h2 className="font-display text-[2rem] sm:text-[2.5rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-5">
                {c.closingTitle}
              </h2>
              <p className="text-[1rem] text-brand-linen/80 leading-[1.7] mb-8 max-w-[520px] mx-auto">{c.closingBody}</p>
              <Link href={c.waitlistHref} className="inline-flex items-center px-6 py-3 bg-brand-meadow text-brand-forest text-sm font-semibold rounded-md hover:bg-brand-meadow-light transition-all">
                {c.closingCta}
              </Link>
              <div className="mt-5">
                <Link
                  href={c.locale === "nl" ? "/nl/data-sources" : "/data-sources"}
                  className="inline-flex items-center gap-1.5 text-[0.78rem] text-brand-sage hover:text-brand-meadow transition-colors"
                >
                  {c.locale === "nl" ? "Bekijk alle databronnen" : "See all data sources"}
                  <svg viewBox="0 0 16 16" className="w-3 h-3 stroke-current fill-none" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="py-8 border-t border-brand-border bg-brand-pine">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* py-3 -my-3 on footer links: ≥40px tap targets without layout shift */}
          <Link href={c.homeHref} className="py-3 -my-3 font-display text-[1.1rem] font-semibold text-brand-muted tracking-[0.02em]">
            PlotScore
          </Link>
          <div className="flex items-center gap-6 text-[0.75rem] text-brand-muted">
            <Link href={c.homeHref} className="py-3 -my-3 hover:text-brand-sage transition-colors">{c.footer.home}</Link>
            <span>{c.footer.location}</span>
            <Link href="/legal#privacy" className="py-3 -my-3 hover:text-brand-sage transition-colors">{c.footer.privacy}</Link>
            <Link href="/legal#terms" className="py-3 -my-3 hover:text-brand-sage transition-colors">{c.footer.terms}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   Dimension Visualizations (compact, conceptual)
────────────────────────────────────────────────────────── */

function DimensionViz({ dim }: { dim: Dimension }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  return (
    <div ref={ref} className="border border-brand-border rounded-xl p-5" style={{ background: "rgb(var(--c-surface) / 0.5)" }}>
      {/* Mini score arc */}
      <div className="flex justify-center mb-4">
        <MiniArc score={dimScores[dim.id] ?? 78} accent={dim.accent} inView={inView} />
      </div>

      {/* Factor summary bars */}
      <div className="space-y-2.5">
        {dim.exampleFactors.slice(0, 3).map((f, i) => {
          const barWidth = [82, 68, 74, 91][i % 4]; // decorative widths
          return (
            <div key={f.name}>
              <div className="flex justify-between mb-0.5">
                <span className="text-[0.7rem] text-brand-muted">{f.name}</span>
                <span className="font-data text-[0.65rem] tabular-nums" style={{ color: `rgb(var(--c-tab-${dim.id === "neighborhood" ? "planning" : dim.id}))` }}>{barWidth}</span>
              </div>
              <div className="h-1 bg-brand-moss rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: inView ? `${barWidth}%` : "0%",
                    background: dim.accent,
                    transitionDelay: `${0.3 + i * 0.1}s`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const dimScores: Record<string, number> = {
  valuation: 82,
  building: 74,
  location: 91,
  neighborhood: 68,
  investment: 73,
};

function MiniArc({ score, accent, inView }: { score: number; accent: string; inView: boolean }) {
  const r = 32;
  const cx = 40;
  const cy = 40;
  const arcAngle = 220;
  const circ = 2 * Math.PI * r;
  const scoreDash = ((score / 100) * arcAngle / 360) * circ;
  const totalDash = (arcAngle / 360) * circ;

  return (
    <svg viewBox="0 0 80 60" className="w-[100px]">
      {/* Track — chrome, so it takes the border token. On `style`, not the
          `stroke=` presentation attribute: an inline style is a plain CSS
          declaration, so var() substitution is guaranteed (in a presentation
          attribute it is engine-dependent, and a miss falls back to the
          initial paint with nothing erroring). Style wins over the attribute
          either way, so there is no conflict. */}
      <circle cx={cx} cy={cy} r={r} fill="none" strokeWidth="4"
        strokeDasharray={`${totalDash} ${circ}`} strokeLinecap="round"
        transform={`rotate(160 ${cx} ${cy})`}
        style={{ stroke: "rgb(var(--c-border))" }}
      />
      {/* Fill */}
      <circle cx={cx} cy={cy} r={r} fill="none"
        stroke={accent} strokeWidth="4"
        strokeDasharray={`${inView ? scoreDash : 0} ${circ}`} strokeLinecap="round"
        transform={`rotate(160 ${cx} ${cy})`}
        style={{ transition: "stroke-dasharray 1s cubic-bezier(0.25,0.1,0.25,1) 0.2s" }}
      />
      {/* Score text */}
      <text x={cx} y={cx - 6} textAnchor="middle" dominantBaseline="central"
        className="font-data" fontSize="16" fontWeight="600" fill={accent}
      >
        {inView ? score : 0}
      </text>
    </svg>
  );
}
